// =====================================================================
// src/lib/pessoal.js — o módulo Pessoal, sem DOM
//   (MdM `renderFuncionarios` 12372 · `_encodeFuncDesc`/`_decodeFuncDesc`
//    11820 · `_mesAnterior` 12527 · `_mudarMesFuncionarios` 12536 ·
//    `modalNovoLancEspecial` 14122)
//
// O que o MdM guarda em `localStorage` (nomes) e em `lancamentos`
// (gastos, com a descrição "FUNC:Nome||Tipo||Nota") aqui são tabelas do
// módulo (0054): `funcionarios`, `tipos_de_gasto_de_pessoal`,
// `gastos_de_pessoal`. O espelho em `lancamentos` é do banco; a tela não
// o vê nem precisa.
//
// Divergências de domínio: o dono pode ser o HOTEL (nível `ambos`); a
// lista de meses vai do primeiro gasto ao ano seguinte ao do hotel (o MdM
// fixa 2024–2028); sem foto (o cadastro de login com foto é o módulo
// `usuarios`); o bônus do frigobar volta com o item 6.
// =====================================================================
import { MESES } from './financeiro.js';

const num = v => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

export const TIPOS_PADRAO = Object.freeze(['Salário', 'Vale Alimentação', 'Vale Transporte', 'INSS', 'Impostos']);
export const SEM_NOME = 'Sem nome / Geral';

// "FUNC:Nome||Tipo||Nota" (11820) — o formato do espelho, que o Relatório
// de Despesa Funcionária (5c) e o Resultado leem
export function codificarDescricao(nome, tipo, nota) { return 'FUNC:' + (nome || '') + '||' + tipo + (nota ? '||' + nota : ''); }
export function decodificarDescricao(descricao) {
  if (!descricao) return { nome: '', tipo: '', nota: '' };
  if (descricao.startsWith('FUNC:')) { const p = descricao.slice(5).split('||'); return { nome: p[0] || '', tipo: p[1] || '', nota: p.slice(2).join('||') }; }
  const p = descricao.split('|');
  return { nome: '', tipo: p[0].trim(), nota: p.slice(1).join('|').trim() };
}

// meses
export const mesDe = iso => (iso || '').slice(0, 7);
export function mesAnterior(ym) { let [a, m] = ym.split('-').map(Number); m -= 1; if (m < 1) { m = 12; a -= 1; } return `${a}-${String(m).padStart(2, '0')}`; }
export function rotuloDoMes(ym) { if (!ym) return ''; const [a, m] = ym.split('-'); return `${MESES[Number(m) - 1]} ${a}`; }
// do primeiro gasto (ou do ano do hotel) até dezembro do ano seguinte, decrescente
export function mesesDisponiveis(gastos, hoje) {
  const anoHoje = Number(hoje.slice(0, 4));
  let anoIni = anoHoje;
  for (const g of gastos || []) if (g.data) anoIni = Math.min(anoIni, Number(g.data.slice(0, 4)));
  const lista = [];
  for (let a = anoHoje + 1; a >= anoIni; a--) for (let m = 12; m >= 1; m--) lista.push(`${a}-${String(m).padStart(2, '0')}`);
  return lista;
}

// os tipos que a tela oferece: os do banco; sem nenhum (dono novo), os 5 padrão
export function tiposParaTela(tipos) { return (tipos && tipos.length) ? tipos.map(t => t.nome) : [...TIPOS_PADRAO]; }

// ── a página (12372) ─────────────────────────────────────────────────
export function gastosDoMes(gastos, mes, funcionarioId) {
  return (gastos || []).filter(g => g.data && g.data.startsWith(mes) && (!funcionarioId || g.funcionario_id === funcionarioId))
    .sort((a, b) => a.data.localeCompare(b.data) || String(a.criado_em || '').localeCompare(String(b.criado_em || '')));
}
export const nomeDoFuncionario = (funcionarios, id) => (id ? ((funcionarios || []).find(f => f.id === id) || {}).nome || '-' : null);
// acumulado por tipo (todos os tipos, mesmo zero — a tela mostra só os > 0), por funcionário e o total
export function resumo(gastosMes, funcionarios, tipos) {
  const porTipo = new Map(tiposParaTela(tipos).map(t => [t, 0]));
  const porFuncionario = new Map();
  let total = 0;
  for (const g of gastosMes) {
    const v = num(g.valor); total += v;
    porTipo.set(g.tipo, (porTipo.get(g.tipo) || 0) + v);
    const n = nomeDoFuncionario(funcionarios, g.funcionario_id) || 'Sem nome';
    porFuncionario.set(n, (porFuncionario.get(n) || 0) + v);
  }
  return { porTipo, porFuncionario, total };
}
// os funcionários do filtro: os que têm gasto no mês + os ativos
export function funcionariosDoFiltro(funcionarios, gastosMes) {
  const ids = new Set(gastosMes.map(g => g.funcionario_id).filter(Boolean));
  return (funcionarios || []).filter(f => f.ativo || ids.has(f.id)).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}
// as iniciais do avatar (12474)
export function iniciais(nome) { return String(nome || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join(''); }

// ── copiar do mês anterior (12536) ───────────────────────────────────
// o mês de destino está vazio? então o mês mais recente, até 12 para trás, que tem gasto
export function mesDeOrigem(gastos, mesDestino) {
  if (gastosDoMes(gastos, mesDestino).length) return null;
  let m = mesDestino;
  for (let i = 0; i < 12; i++) { m = mesAnterior(m); if (gastosDoMes(gastos, m).length) return m; }
  return null;
}
// o que vai para `lancar_gastos_de_pessoal`: o dia do gasto de origem (o banco prende ao fim do mês)
export function itensParaCopiar(gastosOrigem) {
  return gastosOrigem.map(g => ({ dia: Number(g.data.slice(8, 10)), tipo: g.tipo, valor: num(g.valor), funcionario_id: g.funcionario_id || null, observacao: g.observacao || null }));
}

// ── validação dos formulários ────────────────────────────────────────
export function validarGasto({ data, tipo, valor }) {
  const erros = [];
  if (!data) erros.push('Informe a data.');
  if (!tipo || !String(tipo).trim()) erros.push('Informe o tipo de despesa.');
  if (!(num(valor) > 0)) erros.push('Informe o valor.');
  return erros;
}
export function validarFuncionario({ nome }) { return String(nome || '').trim() ? [] : ['Informe o nome.']; }
